import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import SectionPanel from "pages/analytics/components/SectionPanel";
import {
  ChartSkeleton,
  EmptyMessage,
  ErrorBanner,
} from "pages/analytics/components/AnalyticsStates";
import { formatNumber, formatPercent } from "pages/analytics/components/analyticsFormatters";
import { getPipelineDistribution } from "services/funnel-analytics.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;
  return (
    <div className="bg-white p-3 border border-gray-200 shadow-sm rounded-md text-sm">
      <p className="font-medium text-gray-900">{item.label}</p>
      <p className="text-gray-600">Count: {formatNumber(item.count)}</p>
      <p className="text-gray-600">Share: {formatPercent(item.percent)}</p>
    </div>
  );
};

export default function PipelineStageDistribution({ api, filters }) {
  const [distribution, setDistribution] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setDistribution([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getPipelineDistribution(api, { filters });
        if (!cancelled) setDistribution(result?.distribution ?? []);
      } catch {
        if (!cancelled) {
          setDistribution([]);
          setError("Failed to load pipeline distribution. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [filters?.dateRangeType, filters?.customDateRange?.[0], filters?.customDateRange?.[1]]);

  const chartData = useMemo(
    () =>
      distribution.map((item) => ({
        ...item,
        label: `${item.pipelineName} · ${item.stageName}`,
        shortLabel: item.stageName,
      })),
    [distribution]
  );

  const chartHeight = isMobile ? 280 : 360;
  const chartMinWidth =
    isMobile && chartData.length > 0
      ? Math.max(chartData.length * 72, 320)
      : "100%";

  return (
    <SectionPanel title="Pipeline Stage Distribution">
      {error && <div className="mb-4"><ErrorBanner message={error} /></div>}

      {isLoading ? (
        <ChartSkeleton height={chartHeight} />
      ) : isCustomRangeIncomplete(filters) ? (
        <EmptyMessage message="Select start and end dates to view pipeline distribution." />
      ) : error ? (
        <EmptyMessage message="Pipeline distribution unavailable due to a loading error." />
      ) : chartData.length === 0 ? (
        <EmptyMessage message="No pipeline opportunities found." />
      ) : (
        <div className={isMobile ? "overflow-x-auto" : ""}>
          <div style={{ minWidth: chartMinWidth, height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: isMobile ? 60 : 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis
                  dataKey="shortLabel"
                  tick={{ fontSize: isMobile ? 10 : 11, fill: "#6B7280" }}
                  axisLine={{ stroke: "#E5E7EB" }}
                  tickLine={{ stroke: "#E5E7EB" }}
                  angle={isMobile ? -45 : -30}
                  textAnchor="end"
                  height={isMobile ? 80 : 60}
                  interval={0}
                />
                <YAxis
                  tickFormatter={(v) => formatNumber(v)}
                  tick={{ fontSize: isMobile ? 10 : 12, fill: "#6B7280" }}
                  axisLine={{ stroke: "#E5E7EB" }}
                  tickLine={{ stroke: "#E5E7EB" }}
                  width={isMobile ? 48 : 60}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#4F46E5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </SectionPanel>
  );
}
